// useGroupsTree — Phase 2 그룹 트리 TanStack Query 훅.
// queryKey 규칙:
//   ['groups', 'tree', kind?]  — 트리 전체
//   ['groups', 'descendants', id]  — 자손 ID 배열
//   ['groups', 'ancestors', id]    — 조상 ID 배열

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { groupsTreeApi, type MoveGroupInput, type ReorderGroupsInput } from '../api/groupsTree';
import type { GroupKind } from '../api/types';

// ─── queryKey 상수 ───────────────────────────────────────────

export const GROUPS_TREE_KEY = ['groups', 'tree'] as const;
export const GROUPS_DESCENDANTS_KEY = (id: number) => ['groups', 'descendants', id] as const;
export const GROUPS_ANCESTORS_KEY = (id: number) => ['groups', 'ancestors', id] as const;

// ─── 조회 훅 ─────────────────────────────────────────────────

/**
 * 그룹 트리 조회 훅.
 * kind 미지정 시 전체 forest 반환.
 */
export function useGroupsTree(kind?: GroupKind) {
  return useQuery({
    queryKey: kind ? [...GROUPS_TREE_KEY, kind] : GROUPS_TREE_KEY,
    queryFn: () => groupsTreeApi.getTree(kind),
  });
}

/**
 * 특정 그룹의 자손 ID 배열 조회 훅.
 * id=null이면 비활성 (enabled=false).
 */
export function useGroupDescendants(id: number | null) {
  return useQuery({
    queryKey: id !== null ? GROUPS_DESCENDANTS_KEY(id) : ['groups', 'descendants', null],
    queryFn: () => groupsTreeApi.descendants(id!).then((r) => r.ids),
    enabled: id !== null,
  });
}

/**
 * 특정 그룹의 조상 ID 배열 조회 훅 (breadcrumb용).
 * id=null이면 비활성.
 */
export function useGroupAncestors(id: number | null) {
  return useQuery({
    queryKey: id !== null ? GROUPS_ANCESTORS_KEY(id) : ['groups', 'ancestors', null],
    queryFn: () => groupsTreeApi.ancestors(id!).then((r) => r.ids),
    enabled: id !== null,
  });
}

// ─── 뮤테이션 훅 ─────────────────────────────────────────────

/**
 * 그룹 트리 뮤테이션 훅 모음.
 * move: 그룹 이동 후 트리 전체 + 영향받은 자손/조상 캐시 무효화.
 * reorder: 형제 순서 변경 후 트리 전체 캐시 무효화.
 */
export function useGroupTreeMutations() {
  const qc = useQueryClient();

  // 트리 전체 무효화 헬퍼
  const invalidateTree = () => qc.invalidateQueries({ queryKey: ['groups', 'tree'] });

  const moveGroup = useMutation({
    mutationFn: ({ id, body }: { id: number; body: MoveGroupInput }) =>
      groupsTreeApi.moveGroup(id, body),
    onSuccess: (_data, variables) => {
      // 트리 전체 + 이동한 그룹의 descendants/ancestors 캐시 무효화
      invalidateTree();
      qc.invalidateQueries({ queryKey: GROUPS_DESCENDANTS_KEY(variables.id) });
      qc.invalidateQueries({ queryKey: GROUPS_ANCESTORS_KEY(variables.id) });
      // items 캐시도 무효화 — group_scope=descendants로 조회 중인 경우 영향 있음
      qc.invalidateQueries({ queryKey: ['items'] });
    },
  });

  const reorderGroups = useMutation({
    mutationFn: (body: ReorderGroupsInput) => groupsTreeApi.reorderGroups(body),
    onSuccess: () => {
      invalidateTree();
    },
  });

  return { moveGroup, reorderGroups };
}
